import {useCallback, useState} from 'react'
import {ScrollView, Text, TextInput, TouchableOpacity, View} from 'react-native'
import * as Haptics from 'expo-haptics'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import {StudioTextField} from '#/components/studio/StudioTextField'
import {useStudioChrome} from '#/themes-studio/useChrome'
import {type Slide} from '#/types/capsule'

// ── Token helpers ─────────────────────────────────────────────────────────────

type WCToken = {id: string; text: string}

function makeId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
}

function wc_tokenize(text: string): WCToken[] {
  return text
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(w => ({id: makeId(), text: w}))
}

function wc_initFromSlide(slide: Slide): {
  rawText: string
  tokens: WCToken[]
  selectedIds: string[]
  distractors: string
  altMap: Record<string, string[]>
} {
  if (slide.type === 'drag_words') {
    const template = slide.dragWordsTemplate ?? ''
    const tokens: WCToken[] = []
    const selectedIds: string[] = []
    template.split(/(\[[^\]]+\])/).forEach(part => {
      if (/^\[.+\]$/.test(part)) {
        const id = makeId()
        tokens.push({id, text: part.slice(1, -1)})
        selectedIds.push(id)
      } else {
        part
          .trim()
          .split(/\s+/)
          .filter(Boolean)
          .forEach(w => {
            tokens.push({id: makeId(), text: w})
          })
      }
    })
    return {
      rawText: tokens.map(t => t.text).join(' '),
      tokens,
      selectedIds,
      distractors: (slide.dragWordsBank ?? []).join(', '),
      altMap: {},
    }
  }
  if (slide.type === 'fill_blanks') {
    const para = slide.fillBlanksParagraph ?? ''
    const answers = slide.fillBlanksAnswers ?? []
    const tokens: WCToken[] = []
    const selectedIds: string[] = []
    const altMap: Record<string, string[]> = {}
    let ai = 0
    para.split('___').forEach((part, i, arr) => {
      part
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .forEach(w => tokens.push({id: makeId(), text: w}))
      if (i < arr.length - 1) {
        const ans = answers[ai]
        const id = makeId()
        tokens.push({id, text: ans?.answer ?? `answer${ai + 1}`})
        selectedIds.push(id)
        if (ans?.alternatives?.length) altMap[id] = [...ans.alternatives]
        ai++
      }
    })
    return {
      rawText: tokens.map(t => t.text).join(' '),
      tokens,
      selectedIds,
      distractors: '',
      altMap,
    }
  }
  if (slide.type === 'mark_words') {
    const text = slide.markWordsText ?? ''
    const correct = new Set(
      (slide.markWordsCorrect ?? []).map(w => w.toLowerCase().trim()),
    )
    const tokens = wc_tokenize(text)
    const selectedIds = tokens
      .filter(t =>
        correct.has(
          t.text
            .replace(/[.,;:!?]+$/, '')
            .toLowerCase()
            .trim(),
        ),
      )
      .map(t => t.id)
    return {rawText: text, tokens, selectedIds, distractors: '', altMap: {}}
  }
  return {rawText: '', tokens: [], selectedIds: [], distractors: '', altMap: {}}
}

// ── WordClickEditor ───────────────────────────────────────────────────────────
// Shared editor for drag_words, fill_blanks, and mark_words.
// Two-step: (1) type paragraph text, (2) tap tokens to select blanks/correct words.
// ─────────────────────────────────────────────────────────────────────────────

export function WordClickEditor({
  slide,
  primaryColor,
  onUpdate,
  footer,
}: {
  slide: Slide
  primaryColor: string
  onUpdate: (data: Partial<Slide>) => void
  footer?: React.ReactNode
}) {
  const chrome = useStudioChrome()
  const {t: l} = useLingui()
  const hasData =
    (slide.type === 'drag_words' && !!(slide.dragWordsTemplate ?? '').trim()) ||
    (slide.type === 'fill_blanks' &&
      !!(slide.fillBlanksParagraph ?? '').trim()) ||
    (slide.type === 'mark_words' && !!(slide.markWordsText ?? '').trim())

  const [step, setStep] = useState<'input' | 'interactive'>(
    hasData ? 'interactive' : 'input',
  )
  const [rawText, setRawText] = useState(() => wc_initFromSlide(slide).rawText)
  const [tokens, setTokens] = useState<WCToken[]>(
    () => wc_initFromSlide(slide).tokens,
  )
  const [selectedIds, setSelectedIds] = useState<string[]>(
    () => wc_initFromSlide(slide).selectedIds,
  )
  const [lastClickedId, setLastClickedId] = useState<string | null>(null)
  const [distList, setDistList] = useState<string[]>(() => {
    const d = wc_initFromSlide(slide).distractors
    return d
      ? d
          .split(',')
          .map(w => w.trim())
          .filter(Boolean)
      : []
  })
  const [showDistInput, setShowDistInput] = useState(false)
  const [newDistWord, setNewDistWord] = useState('')
  const [altMap, setAltMap] = useState<Record<string, string[]>>(
    () => wc_initFromSlide(slide).altMap,
  )
  const [newAltInput, setNewAltInput] = useState<Record<string, string>>({})

  // ── Sync to parent ────────────────────────────────────────────────────────

  const doSync = useCallback(
    (
      toks: WCToken[],
      selIds: string[],
      dList: string[],
      alts: Record<string, string[]>,
    ) => {
      if (slide.type === 'drag_words') {
        const template = toks
          .map(t => (selIds.includes(t.id) ? `[${t.text}]` : t.text))
          .join(' ')
        onUpdate({dragWordsTemplate: template, dragWordsBank: dList})
      } else if (slide.type === 'fill_blanks') {
        const para = toks
          .map(t => (selIds.includes(t.id) ? '___' : t.text))
          .join(' ')
        const answers = toks
          .filter(t => selIds.includes(t.id))
          .map(t => ({answer: t.text, alternatives: alts[t.id] ?? []}))
        onUpdate({fillBlanksParagraph: para, fillBlanksAnswers: answers})
      } else if (slide.type === 'mark_words') {
        const text = toks.map(t => t.text).join(' ')
        const correct = toks.filter(t => selIds.includes(t.id)).map(t => t.text)
        onUpdate({markWordsText: text, markWordsCorrect: correct})
      }
    },
    [slide.type, onUpdate],
  )

  // ── Step 1 → 2 ───────────────────────────────────────────────────────────

  const handleConfirm = () => {
    if (!rawText.trim()) return
    const toks = wc_tokenize(rawText)
    setTokens(toks)
    setSelectedIds([])
    setLastClickedId(null)
    setAltMap({})
    setStep('interactive')
    doSync(toks, [], distList, {})
  }

  // ── Token click ───────────────────────────────────────────────────────────

  const handleTokenClick = (tok: WCToken) => {
    void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light)

    if (selectedIds.includes(tok.id)) {
      // Deselect. If merged, split back.
      const parts = tok.text.split(/\s+/).filter(Boolean)
      let newToks = tokens
      if (parts.length > 1) {
        const idx = tokens.findIndex(t => t.id === tok.id)
        const split = parts.map(p => ({id: makeId(), text: p}))
        newToks = [...tokens.slice(0, idx), ...split, ...tokens.slice(idx + 1)]
        setTokens(newToks)
      }
      const newSel = selectedIds.filter(id => id !== tok.id)
      setSelectedIds(newSel)
      setLastClickedId(null)
      doSync(newToks, newSel, distList, altMap)
      return
    }

    // Check whether to merge with last-clicked adjacent token.
    const tokIdx = tokens.findIndex(t => t.id === tok.id)
    const lastIdx =
      lastClickedId != null ? tokens.findIndex(t => t.id === lastClickedId) : -1
    const canMerge =
      lastIdx !== -1 &&
      Math.abs(tokIdx - lastIdx) === 1 &&
      selectedIds.includes(lastClickedId!)

    if (canMerge) {
      const loIdx = Math.min(tokIdx, lastIdx)
      const t0 = tokens[loIdx]
      const t1 = tokens[loIdx + 1]
      const merged: WCToken = {id: makeId(), text: `${t0.text} ${t1.text}`}
      const newToks = [
        ...tokens.slice(0, loIdx),
        merged,
        ...tokens.slice(loIdx + 2),
      ]
      const newSel = [
        ...selectedIds.filter(id => id !== lastClickedId),
        merged.id,
      ]
      setTokens(newToks)
      setSelectedIds(newSel)
      setLastClickedId(null)
      doSync(newToks, newSel, distList, altMap)
    } else {
      const newSel = [...selectedIds, tok.id]
      setSelectedIds(newSel)
      setLastClickedId(tok.id)
      doSync(tokens, newSel, distList, altMap)
    }
  }

  const orderedSelected = tokens.filter(t => selectedIds.includes(t.id))
  const selSet = new Set(selectedIds)

  // ── Distractor helpers ────────────────────────────────────────────────────

  const addDistractor = () => {
    const v = newDistWord.trim()
    if (!v) {
      setShowDistInput(false)
      return
    }
    const next = [...distList, v]
    setDistList(next)
    setNewDistWord('')
    setShowDistInput(false)
    doSync(tokens, selectedIds, next, altMap)
  }

  const removeDistractor = (i: number) => {
    const next = distList.filter((_, j) => j !== i)
    setDistList(next)
    doSync(tokens, selectedIds, next, altMap)
  }

  // ── Step 1: paragraph input ───────────────────────────────────────────────

  if (step === 'input') {
    const label =
      slide.type === 'drag_words'
        ? 'DRAG WORDS — PARAGRAPH'
        : slide.type === 'fill_blanks'
          ? 'FILL IN BLANKS — PARAGRAPH'
          : 'MARK WORDS — PARAGRAPH'
    const hint =
      slide.type === 'mark_words'
        ? 'Type the full text, then tap Confirm to select which words students must click.'
        : 'Type the full sentence/paragraph, then tap Confirm to choose which words become blanks.'
    const ph =
      slide.type === 'drag_words'
        ? 'The capital of France is Paris.'
        : 'Mars and Jupiter are planets. The sun is a star.'

    return (
      <View style={{padding: 16, gap: 10}}>
        <Text
          style={{
            color: chrome.textFaint,
            fontSize: 10,
            fontWeight: '700',
            letterSpacing: 0.9,
          }}>
          {label}
        </Text>
        <Text style={{color: chrome.textFaint, fontSize: 11}}>{hint}</Text>
        <View
          style={{
            borderRadius: 12,
            borderWidth: 1.5,
            borderColor: primaryColor + '55',
            backgroundColor: chrome.fill,
            paddingHorizontal: 14,
            paddingVertical: 10,
          }}>
          <StudioTextField
            accessibilityLabel="Text input field"
            accessibilityHint={undefined}
            style={{
              color: chrome.text,
              fontSize: 14,
              paddingVertical: 0,
              lineHeight: 20,
            }}
            placeholder={ph}
            placeholderTextColor={chrome.textFaint}
            value={rawText}
            onChangeText={setRawText}
            textAlignVertical="top"
          />
        </View>
        <TouchableOpacity
          accessibilityRole="button"
          onPress={handleConfirm}
          disabled={!rawText.trim()}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            paddingVertical: 12,
            borderRadius: 12,
            borderWidth: 1.5,
            borderColor: rawText.trim() ? primaryColor : chrome.borderStrong,
            backgroundColor: rawText.trim()
              ? primaryColor + '22'
              : 'transparent',
          }}>
          <Feather
            name="check"
            size={14}
            color={rawText.trim() ? primaryColor : chrome.textFaint}
          />
          <Text
            style={{
              color: rawText.trim() ? primaryColor : chrome.textFaint,
              fontSize: 13,
              fontWeight: '700',
            }}>
            Confirm paragraph
          </Text>
        </TouchableOpacity>
      </View>
    )
  }

  // ── Step 2: interactive paragraph ─────────────────────────────────────────

  const actionHint =
    slide.type === 'mark_words'
      ? 'Tap a word to mark it as correct. Tap two adjacent words consecutively to group them.'
      : 'Tap a word to make it a blank. Tap two adjacent words consecutively to group them.'

  return (
    <ScrollView
      style={{flex: 1}}
      contentContainerStyle={{padding: 16, gap: 10, paddingBottom: 40}}
      keyboardShouldPersistTaps="handled">
      <Text
        style={{
          color: chrome.textFaint,
          fontSize: 10,
          fontWeight: '700',
          letterSpacing: 0.9,
        }}>
        {slide.type === 'mark_words'
          ? 'TAP WORDS TO MARK AS CORRECT'
          : 'TAP WORDS TO CREATE BLANKS'}
      </Text>
      <Text style={{color: chrome.textFaint, fontSize: 11}}>{actionHint}</Text>

      {/* Interactive paragraph zone */}
      <View
        style={{
          backgroundColor: chrome.fill,
          borderRadius: 14,
          padding: 14,
          borderWidth: 1.5,
          borderColor: primaryColor + '44',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 4,
          rowGap: 8,
          alignItems: 'center',
        }}>
        {tokens.length === 0 && (
          <Text style={{color: chrome.textFaint, fontSize: 13}}>
            No words yet. Go back and enter text above.
          </Text>
        )}
        {tokens.map(tok => {
          const isSel = selSet.has(tok.id)
          const isLast = tok.id === lastClickedId

          if (isSel && slide.type === 'mark_words') {
            return (
              <TouchableOpacity
                accessibilityRole="button"
                key={tok.id}
                onPress={() => handleTokenClick(tok)}
                style={{
                  paddingHorizontal: 10,
                  paddingVertical: 5,
                  borderRadius: 14,
                  borderWidth: 2,
                  borderColor: primaryColor,
                  backgroundColor: primaryColor + '22',
                }}>
                <Text
                  style={{
                    color: primaryColor,
                    fontSize: 14,
                    fontWeight: '700',
                  }}>
                  {tok.text}
                </Text>
              </TouchableOpacity>
            )
          }

          if (isSel) {
            const blankW = Math.max(tok.text.length * 8 + 16, 44)
            return (
              <TouchableOpacity
                accessibilityRole="button"
                key={tok.id}
                onPress={() => handleTokenClick(tok)}
                style={{
                  width: blankW,
                  height: 28,
                  borderRadius: 7,
                  borderWidth: 1.5,
                  borderStyle: 'dashed',
                  borderColor: primaryColor + '99',
                  backgroundColor: primaryColor + '11',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <Text style={{color: primaryColor + '77', fontSize: 11}}>
                  _
                </Text>
              </TouchableOpacity>
            )
          }

          return (
            <TouchableOpacity
              accessibilityRole="button"
              key={tok.id}
              onPress={() => handleTokenClick(tok)}
              style={{
                paddingHorizontal: isLast ? 8 : 2,
                paddingVertical: isLast ? 3 : 1,
                borderRadius: 8,
                borderWidth: isLast ? 1 : 0,
                borderColor: primaryColor + '55',
                backgroundColor: isLast ? primaryColor + '22' : 'transparent',
              }}>
              <Text
                style={{
                  color: isLast ? primaryColor : chrome.text,
                  fontSize: 14,
                  lineHeight: 22,
                }}>
                {tok.text}
              </Text>
            </TouchableOpacity>
          )
        })}
      </View>

      {/* Edit bar */}
      <TouchableOpacity
        accessibilityRole="button"
        onPress={() => {
          setStep('input')
          setLastClickedId(null)
        }}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          alignSelf: 'flex-start',
          paddingHorizontal: 10,
          paddingVertical: 6,
          borderRadius: 10,
          backgroundColor: chrome.fill,
          borderWidth: 1,
          borderColor: chrome.borderStrong,
        }}>
        <Feather name="edit-2" size={12} color={chrome.textFaint} />
        <Text style={{color: chrome.textFaint, fontSize: 12}}>
          Edit paragraph text
        </Text>
      </TouchableOpacity>

      {/* Drag Words: word bank preview + distractors */}
      {slide.type === 'drag_words' && (
        <View style={{gap: 8, marginTop: 2}}>
          {orderedSelected.length > 0 && (
            <>
              <Text
                style={{
                  color: chrome.textFaint,
                  fontSize: 10,
                  fontWeight: '700',
                  letterSpacing: 0.9,
                }}>
                WORD BANK PREVIEW
              </Text>
              <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 6}}>
                {orderedSelected.map(t => (
                  <TouchableOpacity
                    accessibilityRole="button"
                    key={t.id}
                    onPress={() => handleTokenClick(t)}
                    style={{
                      paddingHorizontal: 12,
                      paddingVertical: 6,
                      borderRadius: 20,
                      borderWidth: 1.5,
                      borderColor: primaryColor + '88',
                      backgroundColor: primaryColor + '18',
                    }}>
                    <Text
                      style={{
                        color: primaryColor,
                        fontSize: 13,
                        fontWeight: '600',
                      }}>
                      {t.text}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </>
          )}
          <Text
            style={{
              color: chrome.textFaint,
              fontSize: 10,
              fontWeight: '700',
              letterSpacing: 0.9,
              marginTop: 2,
            }}>
            DISTRACTOR WORDS (optional)
          </Text>
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 6,
              alignItems: 'center',
            }}>
            {distList.map((w, i) => (
              <View
                key={i}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 5,
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 20,
                  borderWidth: 1.5,
                  borderColor: primaryColor + '88',
                  backgroundColor: primaryColor + '18',
                }}>
                <Text
                  style={{
                    color: primaryColor,
                    fontSize: 13,
                    fontWeight: '600',
                  }}>
                  {w}
                </Text>
                <TouchableOpacity
                  accessibilityRole="button"
                  onPress={() => removeDistractor(i)}
                  hitSlop={{top: 6, bottom: 6, left: 4, right: 4}}>
                  <Feather name="x" size={11} color={primaryColor + 'cc'} />
                </TouchableOpacity>
              </View>
            ))}
            {showDistInput ? (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6,
                  paddingHorizontal: 10,
                  paddingVertical: 5,
                  borderRadius: 20,
                  borderWidth: 1.5,
                  borderColor: primaryColor + '55',
                  backgroundColor: chrome.fill,
                }}>
                <TextInput
                  accessibilityLabel="Text input field"
                  accessibilityHint={undefined}
                  autoFocus
                  style={{
                    color: chrome.text,
                    fontSize: 13,
                    paddingVertical: 0,
                    minWidth: 60,
                    maxWidth: 140,
                  }}
                  placeholder={l`word…`}
                  placeholderTextColor={chrome.textFaint}
                  value={newDistWord}
                  onChangeText={setNewDistWord}
                  onSubmitEditing={addDistractor}
                  returnKeyType="done"
                  onBlur={() => {
                    if (!newDistWord.trim()) setShowDistInput(false)
                  }}
                />
                {newDistWord.trim() ? (
                  <TouchableOpacity
                    accessibilityRole="button"
                    onPress={addDistractor}
                    hitSlop={{top: 6, bottom: 6, left: 4, right: 4}}>
                    <Feather name="check" size={13} color={primaryColor} />
                  </TouchableOpacity>
                ) : null}
              </View>
            ) : (
              <TouchableOpacity
                accessibilityRole="button"
                onPress={() => setShowDistInput(true)}
                style={{
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 20,
                  borderWidth: 1.5,
                  borderStyle: 'dashed',
                  borderColor: primaryColor + '55',
                  backgroundColor: 'transparent',
                }}>
                <Text style={{color: primaryColor + 'aa', fontSize: 13}}>
                  + word
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      )}

      {/* Fill Blanks: compact inline answer list */}
      {slide.type === 'fill_blanks' && orderedSelected.length > 0 && (
        <View style={{gap: 8, marginTop: 2}}>
          <Text
            style={{
              color: chrome.textFaint,
              fontSize: 10,
              fontWeight: '700',
              letterSpacing: 0.9,
            }}>
            ANSWERS (in order of appearance)
          </Text>
          {orderedSelected.map((tok, i) => {
            const alts = altMap[tok.id] ?? []
            const inputVal = newAltInput[tok.id] ?? ''
            const addAlt = () => {
              const v = inputVal.trim()
              if (!v) return
              const newAlts = {
                ...altMap,
                [tok.id]: [...(altMap[tok.id] ?? []), v],
              }
              setAltMap(newAlts)
              setNewAltInput(p => ({...p, [tok.id]: ''}))
              doSync(tokens, selectedIds, distList, newAlts)
            }
            return (
              <View
                key={tok.id}
                style={{
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 6,
                  rowGap: 6,
                }}>
                <View
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    backgroundColor: primaryColor + '22',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <Text
                    style={{
                      color: primaryColor,
                      fontSize: 11,
                      fontWeight: '800',
                    }}>
                    {i + 1}
                  </Text>
                </View>
                <TouchableOpacity
                  accessibilityRole="button"
                  onPress={() => handleTokenClick(tok)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 5,
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                    borderRadius: 14,
                    borderWidth: 1.5,
                    borderColor: primaryColor + '88',
                    backgroundColor: primaryColor + '18',
                  }}>
                  <Text
                    style={{
                      color: primaryColor,
                      fontSize: 13,
                      fontWeight: '700',
                    }}>
                    {tok.text}
                  </Text>
                  <Feather name="x" size={11} color={primaryColor + 'aa'} />
                </TouchableOpacity>
                {alts.map((alt, ai) => (
                  <View
                    key={ai}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 4,
                      paddingHorizontal: 8,
                      paddingVertical: 4,
                      borderRadius: 12,
                      backgroundColor: chrome.fill,
                      borderWidth: 1,
                      borderColor: chrome.borderStrong,
                    }}>
                    <Text style={{color: chrome.textMuted, fontSize: 12}}>
                      {alt}
                    </Text>
                    <TouchableOpacity
                      accessibilityRole="button"
                      hitSlop={{top: 4, bottom: 4, left: 4, right: 4}}
                      onPress={() => {
                        const newAlts = {
                          ...altMap,
                          [tok.id]: alts.filter((_, j) => j !== ai),
                        }
                        setAltMap(newAlts)
                        doSync(tokens, selectedIds, distList, newAlts)
                      }}>
                      <Feather name="x" size={10} color={chrome.textFaint} />
                    </TouchableOpacity>
                  </View>
                ))}
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 4,
                    paddingHorizontal: 8,
                    paddingVertical: 4,
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: chrome.borderStrong,
                    backgroundColor: chrome.fill,
                  }}>
                  <TextInput
                    accessibilityLabel="Text input field"
                    accessibilityHint={undefined}
                    style={{
                      color: chrome.textMuted,
                      fontSize: 12,
                      paddingVertical: 0,
                      minWidth: 110,
                    }}
                    placeholder={l`+ accepted variant`}
                    placeholderTextColor={chrome.textFaint}
                    value={inputVal}
                    onChangeText={t =>
                      setNewAltInput(p => ({...p, [tok.id]: t}))
                    }
                    onSubmitEditing={addAlt}
                    returnKeyType="done"
                  />
                  {inputVal.trim() ? (
                    <TouchableOpacity
                      accessibilityRole="button"
                      onPress={addAlt}
                      hitSlop={{top: 4, bottom: 4, left: 4, right: 4}}>
                      <Feather name="check" size={12} color={primaryColor} />
                    </TouchableOpacity>
                  ) : null}
                </View>
              </View>
            )
          })}
        </View>
      )}

      {/* Mark Words: summary count */}
      {slide.type === 'mark_words' && (
        <Text
          style={{
            color:
              orderedSelected.length > 0 ? chrome.success : chrome.textFaint,
            fontSize: 12,
            marginTop: 2,
          }}>
          {orderedSelected.length > 0
            ? `✓ ${orderedSelected.length} word${orderedSelected.length !== 1 ? 's' : ''} marked as correct`
            : 'Tap words above to mark the correct answers.'}
        </Text>
      )}
      {footer}
    </ScrollView>
  )
}
